/**
 * True Label checks in the normal food flow.
 *
 *  - labelCheckForFood: for a food whose numbers came off a package (or were typed from one) and
 *    that has a serving size, rebuilds the per-serving Nutrition Facts as printed (FDA rounding,
 *    21 CFR 101.9(c)) and asks the True Label analyzer whether the printed calories agree with the
 *    printed macros.
 *  - macroCalorieGap: for a hand-typed row, whether its calories agree with its macros (Atwater:
 *    4 kcal/g protein, 4 digestible carbs, 2 fiber, 9 fat, 7 alcohol).
 */
import { analyzeLabel, roundCalories, roundFat, roundGram, type DeclaredGrams, type TrueLabelReport } from '@engine/tef';
import type { Food, NutrientMap } from './types';

/** Sources whose values are a printed label (or typed from one). Lab-measured foods are skipped. */
const LABEL_SOURCES: ReadonlySet<string> = new Set(['usda_branded', 'barcode', 'community', 'user']);

export type LabelTone = 'ok' | 'warn' | 'info';

export const VERDICT_PLAIN: Readonly<Record<TrueLabelReport['verdict'], { text: string; tone: LabelTone }>> = {
  CONSISTENT: { text: 'The label adds up: its calories match its protein, carbs and fat.', tone: 'ok' },
  CONSISTENT_WITH_FIBER_ADJUSTMENT: { text: 'The label adds up once fiber and sugar alcohols are counted at their lower calories.', tone: 'ok' },
  BETWEEN_METHODS: { text: 'The label adds up, with fiber or sugar alcohols counted at part value.', tone: 'ok' },
  LABEL_BELOW_MACROS: { text: 'The printed calories are LOWER than its protein, carbs and fat add up to.', tone: 'warn' },
  LABEL_ABOVE_MACROS: { text: 'The printed calories are HIGHER than its protein, carbs and fat add up to.', tone: 'warn' },
};

export interface FoodLabelCheck {
  report: TrueLabelReport;
  servingLabel: string;
  servingGrams: number;
}

const safe = (f: () => TrueLabelReport): TrueLabelReport | null => {
  try { return f(); } catch { return null; }
};

export function labelCheckForFood(food: Pick<Food, 'source' | 'portions'>, per100: NutrientMap): FoodLabelCheck | null {
  if (!LABEL_SOURCES.has(food.source)) return null;
  const serving = food.portions.find((p) => p.grams > 0);
  if (!serving) return null;
  const k = (key: string): number | undefined => (per100[key] === undefined ? undefined : (per100[key] * serving.grams) / 100);
  const kcal = k('energy_kcal');
  const fat = k('fat_g');
  const carbs = k('carbs_g');
  const protein = k('protein_g');
  if (kcal === undefined || fat === undefined || carbs === undefined || protein === undefined) return null;
  const fiber = k('fiber_g');
  const polyol = k('sugar_alcohols_g');
  const grams = (v: number | undefined): DeclaredGrams | null => (v === undefined ? null : roundGram(v));
  const report = safe(() => analyzeLabel({
    caloriesKcal: roundCalories(kcal), fatG: roundFat(fat), totalCarbG: roundGram(carbs), proteinG: roundGram(protein),
    fiberG: grams(fiber), sugarAlcoholG: grams(polyol), servingSizeG: serving.grams, servingsPerContainer: null,
  }));
  return report ? { report, servingLabel: serving.label, servingGrams: serving.grams } : null;
}

export interface MacroGap {
  /** Calories the macros account for. */
  macroKcal: number;
  /** Typed calories minus macro calories. */
  gapKcal: number;
  /** True when the gap is too large to be rounding. */
  flagged: boolean;
}

/** Calories the macros allow vs the calories typed. Flags gaps over 20% and over 25 kcal. */
export function macroCalorieGap(r: { calories: number; protein_g: number; carbs_g: number; fat_g: number; fiber_g?: number | null; alcohol_g?: number | null }): MacroGap | null {
  const fiber: number = Math.min(r.fiber_g ?? 0, r.carbs_g);
  const macroKcal: number = 4 * r.protein_g + 4 * (r.carbs_g - fiber) + 2 * fiber + 9 * r.fat_g + 7 * (r.alcohol_g ?? 0);
  if (!(r.calories > 0) && !(macroKcal > 0)) return null;
  const gapKcal: number = r.calories - macroKcal;
  const base: number = Math.max(r.calories, macroKcal);
  return { macroKcal, gapKcal, flagged: Math.abs(gapKcal) > 25 && Math.abs(gapKcal) / base > 0.2 };
}
